import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import axios, { AxiosError } from "axios";
import { BASE_INTEGRATIONS_ROUTE } from "src/constants/api";

// this type is a subset of the full LabcorpCompendiumItem.
// The whole data will be present (this is javascript after all) but the type will define just of what's useful/used.
export type LabcorpCompendium = {
    orderCode: string;
    orderCodeName: string;
    orderable: string;
    aoeCode: string;
};

/**
 * Representation of a Labcorp AOE (Additional Order Entry) listing.
 */
export type LabcorpAoe = {
    orderCode: string;
    orderName: string;
    sequence: string;
    answerReq: string;
    aoeQuestionCode: string;
    aoeName: string;
    aoeQuestion: string;
    units: string;
    answerType: string;
    editMask: string;
    maxAnswerLength: string;
    pickCode: string;
    pickDescription: string;
    combinedList: string;
    maxNumAnswers: string;
    twoDField: string;
    system: string;
};

const labcorpCompendiumApi = createApi({
    reducerPath: "lobbie/staff/services/labcorp-compendium",
    baseQuery: fetchBaseQuery({}),
    endpoints: (builder) => ({
        getLabcorpCompendiumByCode: builder.query<
            LabcorpCompendium[] | undefined,
            { code: string; exact?: boolean }
        >({
            queryFn: async ({ code, exact }) => {
                const url = `${BASE_INTEGRATIONS_ROUTE}/labcorp/compendium/ordering?code=${code}&exactMatch=${exact ?? "false"}`;
                try {
                    const response = await axios.get<LabcorpCompendium[]>(url, {
                        withCredentials: true,
                    });
                    return { data: response.data };
                } catch (error) {
                    const axiosError = error as AxiosError;
                    return {
                        data: undefined,
                        error: {
                            status: axiosError.status ?? 0,
                            data: axiosError.response?.data,
                        },
                    };
                }
            },
        }),
        getLabcorpCompendiumByName: builder.query<
            LabcorpCompendium[] | undefined,
            { name: string; exact?: boolean }
        >({
            queryFn: async ({ name, exact }) => {
                const url = `${BASE_INTEGRATIONS_ROUTE}/labcorp/compendium/ordering?name=${name}&exactMatch=${exact ?? "false"}`;
                try {
                    const response = await axios.get<LabcorpCompendium[]>(url, {
                        withCredentials: true,
                    });
                    return { data: response.data };
                } catch (error) {
                    const axiosError = error as AxiosError;
                    return {
                        data: undefined,
                        error: {
                            status: axiosError.status ?? 0,
                            data: axiosError.response?.data,
                        },
                    };
                }
            },
        }),
        getLabcorpAoeListForCode: builder.query<LabcorpAoe[] | undefined, string>({
            queryFn: async (code) => {
                const url = `${BASE_INTEGRATIONS_ROUTE}/labcorp/compendium/${code}/aoes`;
                try {
                    const response = await axios.get<LabcorpAoe[]>(url, {
                        withCredentials: true,
                    });
                    return { data: response.data };
                } catch (error) {
                    const axiosError = error as AxiosError;
                    return {
                        data: undefined,
                        error: {
                            status: axiosError.status ?? 0,
                            data: axiosError.response?.data,
                        },
                    };
                }
            },
        }),
        getLabcorpAoeListForCodeList: builder.query<LabcorpAoe[] | undefined, string[]>({
            queryFn: async (codes) => {
                const url = `${BASE_INTEGRATIONS_ROUTE}/labcorp/compendium/aoes?codes=${codes.join(",")}`;
                try {
                    const response = await axios.get<LabcorpAoe[]>(url, {
                        withCredentials: true,
                    });
                    return { data: response.data };
                } catch (error) {
                    const axiosError = error as AxiosError;
                    return {
                        data: undefined,
                        error: {
                            status: axiosError.status ?? 0,
                            data: axiosError.response?.data,
                        },
                    };
                }
            },
        }),
    }),
});

export const {
    useGetLabcorpCompendiumByCodeQuery,
    useGetLabcorpCompendiumByNameQuery,
    useGetLabcorpAoeListForCodeQuery,
    useLazyGetLabcorpAoeListForCodeQuery,
    useGetLabcorpAoeListForCodeListQuery,
    useLazyGetLabcorpAoeListForCodeListQuery,
} = labcorpCompendiumApi;

export default labcorpCompendiumApi;
